import { FiAlertTriangle } from 'react-icons/fi';

export const distractorRecognitionData = {
  courseId: 'distractor-recognition',
  courseType: 'listening-course',
  title: 'Distractor Recognition',
  icon: FiAlertTriangle,
  overview: {
    impact: 'Avoid the traps that cost most students 2-4 marks per test',
    focus: 'Identifying misleading information, self-corrections, and deliberate traps in audio',
    uses: 'All listening sections, critical for Sections 1-3',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Are Distractors?',
      content: `<p>Distractors are <strong>wrong answers that are deliberately mentioned</strong> in the audio to mislead you. IELTS test designers know that many students write down the first thing they hear that seems to match the question — so they place tempting but incorrect information before (or alongside) the real answer.</p>
<p>A distractor is not random noise. It is a <em>plausible-sounding answer</em> that relates to the question but is ultimately corrected, negated, or superseded by the actual answer. Recognising distractors requires you to listen to the <strong>entire relevant segment</strong> before committing to an answer.</p>`,
      points: [
        'Distractors are wrong answers strategically placed in the audio to trap hasty listeners',
        'They usually appear BEFORE the correct answer, tempting you to write too soon',
        'The speaker typically corrects, negates, or changes their mind after stating the distractor',
        'All answer options in multiple-choice questions may be mentioned — only one is correct',
        'Distractors are most common in Sections 1 and 3 where speakers interact and change plans',
      ],
    },
    {
      title: 'Common Distractor Techniques',
      content: `<p>IELTS uses specific, predictable techniques to create distractors. Learning these patterns makes them much easier to spot during the exam.</p>`,
      table: {
        headers: ['Technique', 'How It Works', 'Audio Example'],
        rows: [
          ['Self-correction', 'Speaker states one answer then immediately corrects it', '"The meeting is at 3pm — oh no, sorry, it\'s been moved to 4pm."'],
          ['Negation', 'Speaker mentions an option but rejects it', '"You might think it\'s on the ground floor, but it\'s actually on the second floor."'],
          ['Changed plans', 'An original plan is mentioned, then a different final decision is given', '"We were going to the museum, but we\'ve decided to go to the gallery instead."'],
          ['Multiple mentions', 'Several options are discussed, but only one is the chosen answer', '"There\'s a café, a restaurant, and a food court, but the café is where we\'ll meet."'],
          ['Conditional/hypothetical', 'An option is mentioned as a possibility, not a fact', '"If it rains, we\'d go to the cinema, but the forecast is good so we\'ll have the picnic."'],
          ['Third-party opinion', 'Someone else\'s view is mentioned, but the speaker disagrees', '"Tom suggested Tuesday, but I think Wednesday works better for everyone."'],
        ],
      },
    },
    {
      title: 'Correction and Negation Signals',
      content: `<p>Speakers use specific words and phrases to <strong>signal</strong> that they are correcting or rejecting information. These signals are your early warning system — when you hear them, the <em>real</em> answer is about to come.</p>`,
      points: [
        '<strong>"Actually..."</strong> — The most common correction signal. Whatever follows "actually" is usually the correct answer.',
        '<strong>"No, wait..." / "Oh, sorry..."</strong> — The speaker is actively correcting themselves.',
        '<strong>"I mean..."</strong> — Signals a restatement. The corrected version follows.',
        '<strong>"Well, originally... but now..."</strong> — Contrasts an old plan with the current reality.',
        '<strong>"Not X, but Y"</strong> — Explicit negation followed by the correct answer.',
        '<strong>"On second thought..."</strong> — The speaker is changing their mind. The new choice is correct.',
        '<strong>"Rather than..." / "Instead of..."</strong> — Reject one option in favour of another.',
        '<strong>"It was going to be X, but they changed it to Y"</strong> — Past plan vs current fact.',
      ],
      warning: 'When you hear any correction signal, do NOT write the first piece of information. Wait for the corrected version — that is your answer.',
    },
    {
      title: 'Strategies for Avoiding Traps',
      content: `<p>Even knowing about distractors, it's easy to fall into traps under exam pressure. These strategies build a safety net.</p>`,
      points: [
        '<strong>Don\'t write too quickly:</strong> Resist the urge to write the first answer you hear. Wait until the speaker finishes the relevant segment.',
        '<strong>Listen for finality:</strong> The correct answer often comes after phrases like "so we\'ll go with...", "the final decision is...", "we agreed on..."',
        '<strong>Watch for "but":</strong> In IELTS, information before "but" is frequently the distractor, and information after "but" is the answer.',
        '<strong>Track all options:</strong> In multiple-choice, lightly mark each option as it\'s mentioned, then circle only the one confirmed as correct.',
        '<strong>Beware of repetition:</strong> If an option is mentioned several times, it doesn\'t mean it\'s correct — it might be discussed and then rejected.',
        '<strong>Check for agreement between speakers:</strong> In Section 3, one speaker may suggest X, but the final agreed answer is Y.',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Distractor',
      type: 'select',
      instructions: 'In each scenario, identify which piece of information is the distractor (the wrong answer designed to trap you).',
      questions: [
        {
          text: 'Audio: "The exam is on Thursday — wait, no, I just checked and it\'s actually on Friday." What is the distractor?',
          options: ['Thursday', 'Friday'],
          answer: 'Thursday',
          explanation: '"Thursday" is the distractor. "Wait, no" and "actually" are correction signals. Friday is the real answer.',
        },
        {
          text: 'Audio: "We could take the train, and the bus is also an option, but we\'ve booked a taxi in the end." What is the distractor?',
          options: ['Train and bus', 'Taxi'],
          answer: 'Train and bus',
          explanation: 'Train and bus are distractors — mentioned as options but not chosen. "In the end" confirms taxi as the final decision.',
        },
        {
          text: 'Audio: "The original price was £50, but with the student discount it comes to £35." What is the distractor?',
          options: ['£50', '£35'],
          answer: '£50',
          explanation: '£50 is the distractor (original price). The question likely asks what the student pays, which is £35 after the discount.',
        },
        {
          text: 'Audio: "Sarah recommended the Italian restaurant, but James preferred the Thai place, and they ended up going to the Thai restaurant." What is the distractor?',
          options: ['Italian restaurant', 'Thai restaurant'],
          answer: 'Italian restaurant',
          explanation: 'Italian restaurant is the distractor (Sarah\'s suggestion, not the final choice). "Ended up going to" confirms the Thai restaurant.',
        },
      ],
    },
    {
      title: 'Spot the Correction Signal',
      type: 'select',
      instructions: 'Identify the word or phrase that signals a correction is happening.',
      questions: [
        {
          text: '"The deadline is March 15th. Oh, I mean March 25th — they extended it last week."',
          options: ['"I mean"', '"Last week"', '"The deadline"'],
          answer: '"I mean"',
          explanation: '"I mean" is the classic self-correction signal. It tells you the first date was wrong and the corrected date follows.',
        },
        {
          text: '"You might assume the library is free, but actually there\'s a small membership fee."',
          options: ['"Actually"', '"You might assume"', '"Small"'],
          answer: '"Actually"',
          explanation: '"Actually" signals that the assumption is wrong and the truth follows. "You might assume" sets up the distractor.',
        },
        {
          text: '"We were planning to hold it outdoors. On second thought, we\'ll use the main hall."',
          options: ['"On second thought"', '"Were planning"', '"Main hall"'],
          answer: '"On second thought"',
          explanation: '"On second thought" is a change-of-mind signal. The first plan (outdoors) is the distractor; the hall is the answer.',
        },
        {
          text: '"Not the blue folder — the red one. That\'s where I put the forms."',
          options: ['"Not... the red one"', '"I put"', '"The forms"'],
          answer: '"Not... the red one"',
          explanation: '"Not X — Y" is an explicit negation pattern. "Blue" is rejected and "red" is confirmed.',
        },
      ],
    },
    {
      title: 'Choose the Final Answer After Correction',
      type: 'select',
      instructions: 'Read the audio transcript carefully. What is the CORRECT final answer?',
      questions: [
        {
          text: '"The tour leaves at 9am. No, sorry, that\'s the weekday time. On weekends it leaves at 10am." Question: What time does the weekend tour leave?',
          options: ['10am', '9am'],
          answer: '10am',
          explanation: '9am is the distractor (weekday time). "No, sorry" signals the correction, and "on weekends" specifies the correct context.',
        },
        {
          text: '"The assignment should be about 2,000 words. Well, actually, Professor Mills said she wants at least 2,500." Question: What is the required word count?',
          options: ['2,500 words', '2,000 words'],
          answer: '2,500 words',
          explanation: '2,000 is the distractor. "Actually" + "Professor Mills said" overrides the initial figure with the authoritative answer.',
        },
        {
          text: '"I thought the concert was at the Civic Centre, but I just saw they moved it to the Grand Theatre because of renovations." Question: Where is the concert?',
          options: ['Grand Theatre', 'Civic Centre'],
          answer: 'Grand Theatre',
          explanation: '"I thought" signals uncertainty about the first location. "But" + "they moved it" confirms the Grand Theatre is current and correct.',
        },
        {
          text: '"Maria wanted to major in chemistry, then she considered biology, but she finally enrolled in environmental science." Question: What did Maria study?',
          options: ['Environmental science', 'Chemistry', 'Biology'],
          answer: 'Environmental science',
          explanation: 'Chemistry and biology are sequential distractors. "Finally enrolled in" marks the definitive choice.',
        },
      ],
    },
    {
      title: 'Identify Distractor Technique Used',
      type: 'fill-in',
      instructions: 'Name the distractor technique used in each audio excerpt.',
      questions: [
        {
          text: '"The report was supposed to be due Monday, but they pushed it back to Wednesday." Technique: ___',
          answer: 'changed plans',
          explanation: '"Supposed to be" (original plan) vs "pushed it back to" (new plan) is a classic changed-plans distractor.',
        },
        {
          text: '"Professor Lee suggested using interviews for data collection, but the team decided on surveys instead." Technique: ___',
          answer: 'third-party opinion',
          explanation: 'Professor Lee\'s suggestion is a third-party opinion that the team rejected. The answer is the team\'s decision, not the professor\'s suggestion.',
        },
        {
          text: '"If we had more funding, we\'d hire extra staff, but as things stand, we\'ll redistribute existing workloads." Technique: ___',
          answer: 'conditional',
          explanation: '"If we had more funding" is a hypothetical condition. The real plan ("as things stand") is to redistribute — the conditional option is the distractor.',
        },
      ],
    },
    {
      title: 'Explain Why an Option Is a Distractor',
      type: 'writing',
      instructions: 'Read the scenario and explain in detail why the wrong answer is a distractor and how you would avoid the trap.',
      questions: [
        {
          text: 'Audio: "The original plan was to hold the conference in Building A, which has better facilities. However, due to ongoing maintenance, we\'ve had to relocate it to Building C." A student answers "Building A". Explain why this is wrong and what signals they missed.',
          example: '"Building A" is the distractor because it is the original plan, not the final decision. The correction signal "However" introduces the change, and "due to ongoing maintenance" explains why Building A is no longer available. "We\'ve had to relocate" confirms the change. The student should have waited for the complete sentence and listened for contrast words like "however" before writing their answer. The correct answer is "Building C".',
          tip: 'Explain: (1) what made the wrong answer tempting, (2) what signal words indicated a change, and (3) what the correct answer is and why.',
        },
        {
          text: 'Audio: "There are three payment options: monthly at £30, quarterly at £80, or annually at £250. Most of our members go with the quarterly option because it works out cheaper per month." The question asks: "What is the most popular payment plan?" A student answers "£250/annually". Explain the trap.',
          example: 'The annual plan (£250) is a distractor — it\'s mentioned but never said to be popular. All three options are mentioned (multiple-mentions technique), making each one sound like a potential answer. The key phrase is "Most of our members go with the quarterly option" — "most of our members" directly answers "most popular". The student was probably distracted by the annual plan being the cheapest per month overall, but the question asks about popularity, not value. The answer is "quarterly/£80".',
          tip: 'Focus on what the question actually asks. "Most popular" requires evidence about what people choose, not which option is cheapest.',
        },
      ],
    },
  ],
  tips: [
    'Never write the first answer you hear — wait for the speaker to finish the thought',
    'Train your ear to recognise "actually", "but", "however", "I mean" as correction alerts',
    'In multiple-choice, expect ALL options to be mentioned — only one will be confirmed',
    'If two speakers disagree, listen for the final agreed answer, not individual preferences',
  ],
  dailyPractice: [
    'Listen to dialogues (podcasts, TV shows) and note every time a speaker corrects themselves or changes their mind',
    'Do IELTS practice tests and, for every wrong answer, identify whether a distractor tricked you',
    'Practise with Section 1 conversations where plans change frequently (booking calls, arranging meetings)',
    'Create flashcards of correction signal phrases and quiz yourself until recognition is instant',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Distractor Recognition for IELTS Listening: teaches students to identify and avoid wrong answers deliberately placed in the audio. Covers 6 distractor techniques (self-correction, negation, changed plans, multiple mentions, conditional, third-party opinion), correction signal phrases, and avoidance strategies.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...distractorRecognitionData,
      title: distractorRecognitionData.title + ' (Foundations)',
      overview: { ...distractorRecognitionData.overview, duration: '15-20 minutes' },
      sections: distractorRecognitionData.sections.slice(0, Math.max(2, Math.ceil(distractorRecognitionData.sections.length / 2))),
      exercises: distractorRecognitionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...distractorRecognitionData,
      title: distractorRecognitionData.title + ' (Advanced)',
      overview: { ...distractorRecognitionData.overview, duration: '30-35 minutes' },
      sections: distractorRecognitionData.sections,
      exercises: distractorRecognitionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(distractorRecognitionData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return distractorRecognitionData;
}
